/**
 * MarketingPage — Symphony Marketing Hub
 * Three-tab hub: Content Factory · Campaign Builder · SEO Audit
 */

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  type ColumnDef,
  type SortingState,
  type ColumnFiltersState,
  type PaginationState,
  flexRender,
} from "@tanstack/react-table";

import {
  AlertCircle,
  ArrowUpDown,
  Check,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  ClipboardCopy,
  Loader2,
  RefreshCcw,
  Search,
  Sparkles,
  Tag,
  X,
  Zap,
} from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";

import { cn, truncate } from "@/lib/utils";
import { toast } from "sonner";

// ── types ─────────────────────────────────────────────────────────────────────

interface CuratedProduct {
  id: string;
  tdsynnex_sku: string;
  title: string | null;
  state: string;
  brand_slug: string | null;
  short_description: string | null;
  seo_title: string | null;
  seo_description: string | null;
  body_html: string | null;
  tags?: string[] | null;
  updated_at?: string | null;
}

type SkuStatus = "pending" | "running" | "done" | "error";

interface SkuGenState {
  status: SkuStatus;
  steps: string[];
  error?: string;
}

// campaign types
type CampaignTheme =
  | "product_launch"
  | "tech_refresh"
  | "clearance_sale"
  | "holiday_q4"
  | "back_to_school";

type CampaignAudience =
  | "it_managers"
  | "smb_owners"
  | "procurement_teams";

interface CampaignSku {
  sku: string;
  description: string;
  brand: string;
  msrp: number;
}

interface CampaignOutput {
  email?: { subject: string; body: string };
  linkedin?: { post: string };
  google_ads?: {
    headline1: string;
    headline2: string;
    headline3: string;
    description1: string;
    description2: string;
  };
  product_highlights?: Array<{
    sku: string;
    tagline: string;
    bullets: string[];
  }>;
}

// ── helpers ───────────────────────────────────────────────────────────────────

function copyToClipboard(text: string, label = "Copied") {
  navigator.clipboard.writeText(text).then(
    () => toast.success(`${label} to clipboard`),
    () => toast.error("Clipboard write failed"),
  );
}

function computeSeoScore(p: CuratedProduct): number {
  let score = 0;
  const titleLen = (p.seo_title ?? "").length;
  if (titleLen >= 30 && titleLen <= 60) score += 25;
  const descLen = (p.seo_description ?? "").length;
  if (descLen >= 100 && descLen <= 155) score += 25;
  if ((p.body_html ?? "").length > 500) score += 25;
  if ((p.tags ?? []).length >= 5) score += 25;
  return score;
}

function ScoreBadge({ score }: { score: number }) {
  const color =
    score < 50
      ? "bg-rose-500/20 text-rose-400 border-rose-500/30"
      : score < 75
        ? "bg-amber-500/20 text-amber-400 border-amber-500/30"
        : "bg-emerald-500/20 text-emerald-400 border-emerald-500/30";
  return (
    <Badge className={cn("text-[11px] py-0 px-1.5 font-mono tabular-nums", color)}>
      {score}
    </Badge>
  );
}

function BoolCheck({ val }: { val: boolean }) {
  return val ? (
    <Check className="h-3.5 w-3.5 text-emerald-400" />
  ) : (
    <X className="h-3.5 w-3.5 text-rose-400" />
  );
}

function StateBadge({ state }: { state: string }) {
  const map: Record<string, string> = {
    draft: "bg-slate-500/20 text-slate-400 border-slate-500/30",
    ready: "bg-blue-500/20 text-blue-400 border-blue-500/30",
    published: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30",
    archived: "bg-zinc-500/20 text-zinc-400 border-zinc-500/30",
  };
  return (
    <Badge className={cn("text-[10px] py-0 px-1.5", map[state] ?? "bg-muted/20 text-muted-foreground")}>
      {state}
    </Badge>
  );
}

// ── SSE streaming helper ──────────────────────────────────────────────────────

function streamCurate(
  sku: string,
  onStep: (step: string) => void,
  onDone: (data: Record<string, unknown>) => void,
  onError: (err: string) => void,
): () => void {
  const es = new EventSource(`/api/curate/${encodeURIComponent(sku)}/stream`);

  es.addEventListener("step", (e) => {
    try {
      const d = JSON.parse((e as MessageEvent).data);
      onStep(d.text ?? String(d));
    } catch {
      onStep((e as MessageEvent).data);
    }
  });

  es.addEventListener("done", (e) => {
    try {
      const d = JSON.parse((e as MessageEvent).data);
      onDone(d);
    } catch {
      onDone({});
    }
    es.close();
  });

  es.addEventListener("error", () => {
    onError("Stream error");
    es.close();
  });

  return () => es.close();
}

// ═══════════════════════════════════════════════════════════════════════════════
// Tab 1 — Content Factory
// ═══════════════════════════════════════════════════════════════════════════════

function ContentFactory() {
  const [products, setProducts] = useState<CuratedProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [pagination, setPagination] = useState<PaginationState>({ pageIndex: 0, pageSize: 20 });
  const [rowSelection, setRowSelection] = useState<Record<string, boolean>>({});
  const [preset, setPreset] = useState<"missing_body" | "all">("all");

  // drawer state
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [genStates, setGenStates] = useState<Record<string, SkuGenState>>({});
  const cleanupRefs = useRef<Record<string, () => void>>({});

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch("/api/curated", {
        headers: { Accept: "application/json" },
      });
      if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
      const data = await res.json();
      setProducts(Array.isArray(data) ? data : data.items ?? []);
    } catch (err) {
      toast.error(`Failed to load curated products: ${String(err)}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  // Reset selection when preset changes
  useEffect(() => {
    setRowSelection({});
  }, [preset]);

  const displayProducts = useMemo(() => {
    if (preset === "missing_body") return products.filter((p) => !p.body_html);
    return products;
  }, [products, preset]);

  const columns = useMemo<ColumnDef<CuratedProduct>[]>(
    () => [
      {
        id: "select",
        header: ({ table }) => (
          <Checkbox
            checked={table.getIsAllPageRowsSelected()}
            onCheckedChange={(v) => table.toggleAllPageRowsSelected(!!v)}
            aria-label="Select all"
          />
        ),
        cell: ({ row }) => (
          <Checkbox
            checked={row.getIsSelected()}
            onCheckedChange={(v) => row.toggleSelected(!!v)}
            aria-label="Select row"
          />
        ),
        enableSorting: false,
        size: 36,
      },
      {
        accessorKey: "tdsynnex_sku",
        header: ({ column }) => (
          <button
            className="flex items-center gap-1 text-xs uppercase tracking-wide hover:text-foreground transition-colors"
            onClick={() => column.toggleSorting()}
          >
            SKU <ArrowUpDown className="h-3 w-3" />
          </button>
        ),
        cell: ({ getValue }) => (
          <span className="font-mono text-[11px] text-emerald-400 tabular-nums">
            {String(getValue())}
          </span>
        ),
      },
      {
        accessorKey: "title",
        header: "Title",
        cell: ({ getValue }) => (
          <span className="text-xs text-foreground/90">
            {truncate(String(getValue() ?? "—"), 55)}
          </span>
        ),
      },
      {
        accessorKey: "brand_slug",
        header: "Brand",
        cell: ({ getValue }) => (
          <span className="text-xs capitalize text-muted-foreground">
            {String(getValue() ?? "—")}
          </span>
        ),
      },
      {
        accessorKey: "state",
        header: "State",
        cell: ({ getValue }) => <StateBadge state={String(getValue())} />,
      },
      {
        id: "seo_title",
        header: "SEO Title",
        accessorFn: (r) => !!r.seo_title,
        cell: ({ getValue }) => <BoolCheck val={getValue() as boolean} />,
      },
      {
        id: "body_html",
        header: "Body HTML",
        accessorFn: (r) => !!r.body_html,
        cell: ({ getValue }) => <BoolCheck val={getValue() as boolean} />,
      },
      {
        accessorKey: "updated_at",
        header: "Updated",
        cell: ({ getValue }) => {
          const v = getValue() as string | null;
          if (!v) return <span className="text-xs text-muted-foreground">—</span>;
          return (
            <span className="text-[11px] text-muted-foreground tabular-nums">
              {new Date(v).toLocaleDateString()}
            </span>
          );
        },
      },
    ],
    [],
  );

  const table = useReactTable({
    data: displayProducts,
    columns,
    state: { sorting, columnFilters, pagination, rowSelection },
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onPaginationChange: setPagination,
    onRowSelectionChange: setRowSelection,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    enableRowSelection: true,
  });

  const selectedRows = table.getSelectedRowModel().rows;
  const selectedCount = selectedRows.length;

  async function patchCurated(id: string, patch: Record<string, unknown>) {
    const res = await fetch(`/api/curated/${id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(patch),
    });
    if (!res.ok) throw new Error(`PATCH failed: ${res.status}`);
  }

  function startGeneration() {
    const skus = selectedRows.map((r) => r.original);
    if (skus.length === 0) {
      toast.error("Select at least one product");
      return;
    }

    const initial: Record<string, SkuGenState> = {};
    skus.forEach((p) => {
      initial[p.tdsynnex_sku] = { status: "pending", steps: [] };
    });
    setGenStates(initial);
    setDrawerOpen(true);

    // run sequentially
    let chain = Promise.resolve();
    skus.forEach((product) => {
      chain = chain.then(
        () =>
          new Promise<void>((resolve) => {
            setGenStates((prev) => ({
              ...prev,
              [product.tdsynnex_sku]: { status: "running", steps: [] },
            }));

            const cancel = streamCurate(
              product.tdsynnex_sku,
              (step) => {
                setGenStates((prev) => ({
                  ...prev,
                  [product.tdsynnex_sku]: {
                    ...prev[product.tdsynnex_sku],
                    steps: [...(prev[product.tdsynnex_sku]?.steps ?? []), step],
                  },
                }));
              },
              async (data) => {
                try {
                  await patchCurated(product.id, {
                    title: data.title,
                    body_html: data.body_html,
                    seo_title: data.seo_title,
                    seo_description: data.seo_description,
                  });
                  setGenStates((prev) => ({
                    ...prev,
                    [product.tdsynnex_sku]: {
                      ...prev[product.tdsynnex_sku],
                      status: "done",
                    },
                  }));
                } catch {
                  setGenStates((prev) => ({
                    ...prev,
                    [product.tdsynnex_sku]: {
                      ...prev[product.tdsynnex_sku],
                      status: "error",
                      error: "PATCH failed",
                    },
                  }));
                }
                resolve();
              },
              (err) => {
                setGenStates((prev) => ({
                  ...prev,
                  [product.tdsynnex_sku]: {
                    ...prev[product.tdsynnex_sku],
                    status: "error",
                    error: err,
                  },
                }));
                resolve();
              },
            );
            cleanupRefs.current[product.tdsynnex_sku] = cancel;
          }),
      );
    });

    chain.then(() => {
      toast.success("Content generation complete");
    });
  }

  const titleFilter = (table.getColumn("title")?.getFilterValue() as string) ?? "";

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input
            placeholder="Filter titles…"
            value={titleFilter}
            onChange={(e) => table.getColumn("title")?.setFilterValue(e.target.value)}
            className="pl-8 h-8 text-xs bg-background/50"
          />
        </div>

        {/* Preset toggle */}
        <div className="flex rounded-md border border-border overflow-hidden">
          {(
            [
              { key: "all", label: "All selected" },
              { key: "missing_body", label: "Only missing body" },
            ] as const
          ).map(({ key, label }) => (
            <button
              key={key}
              onClick={() => setPreset(key)}
              className={cn(
                "px-3 py-1 text-xs transition-colors",
                preset === key
                  ? "bg-emerald-600 text-white"
                  : "bg-background/30 text-muted-foreground hover:text-foreground",
              )}
            >
              {label}
            </button>
          ))}
        </div>

        <Button
          size="sm"
          disabled={selectedCount === 0}
          onClick={startGeneration}
          className="bg-emerald-600 hover:bg-emerald-500 text-white gap-1.5"
        >
          <Sparkles className="h-3.5 w-3.5" />
          Generate Copy for Selected ({selectedCount})
        </Button>

        <Button
          size="sm"
          variant="ghost"
          onClick={fetchProducts}
          className="gap-1.5"
        >
          <RefreshCcw className="h-3.5 w-3.5" />
          Refresh
        </Button>
      </div>

      {/* Table */}
      <div className="rounded-lg border border-border overflow-hidden">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((hg) => (
              <TableRow key={hg.id} className="border-border hover:bg-transparent">
                {hg.headers.map((h) => (
                  <TableHead key={h.id} className="text-muted-foreground text-xs h-9">
                    {h.isPlaceholder ? null : flexRender(h.column.columnDef.header, h.getContext())}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-24 text-center">
                  <Loader2 className="h-5 w-5 animate-spin mx-auto text-muted-foreground" />
                </TableCell>
              </TableRow>
            ) : table.getRowModel().rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-20 text-center text-sm text-muted-foreground">
                  No products found.
                </TableCell>
              </TableRow>
            ) : (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() ? "selected" : undefined}
                  className="border-border hover:bg-muted/20 cursor-pointer"
                  onClick={() => row.toggleSelected()}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id} className="py-2">
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <span>
          {table.getFilteredRowModel().rows.length} products · {selectedCount} selected
        </span>
        <div className="flex items-center gap-1">
          <Button
            size="icon"
            variant="ghost"
            className="h-6 w-6"
            disabled={!table.getCanPreviousPage()}
            onClick={() => table.previousPage()}
          >
            <ChevronLeft className="h-3.5 w-3.5" />
          </Button>
          <span>
            {pagination.pageIndex + 1} / {table.getPageCount() || 1}
          </span>
          <Button
            size="icon"
            variant="ghost"
            className="h-6 w-6"
            disabled={!table.getCanNextPage()}
            onClick={() => table.nextPage()}
          >
            <ChevronRight className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>

      {/* Progress Drawer */}
      <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
        <SheetContent side="right" className="w-[420px] bg-zinc-950 border-zinc-800 flex flex-col gap-0 p-0">
          <SheetHeader className="px-6 py-4 border-b border-zinc-800">
            <SheetTitle className="text-sm font-semibold text-foreground flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-emerald-400" />
              Generating content…
            </SheetTitle>
          </SheetHeader>
          <div className="flex-1 overflow-y-auto px-6 py-4 space-y-3">
            {Object.entries(genStates).map(([sku, state]) => (
              <div key={sku} className="rounded-md border border-zinc-800 bg-zinc-900/50 p-3 space-y-2">
                <div className="flex items-center gap-2">
                  <SkuStatusIcon status={state.status} />
                  <span className="font-mono text-[11px] text-emerald-400">{sku}</span>
                  <span className="ml-auto text-[10px] text-muted-foreground capitalize">{state.status}</span>
                </div>
                {state.status === "running" && state.steps.length > 0 && (
                  <div className="pl-5 space-y-0.5">
                    {state.steps.slice(-4).map((s, i) => (
                      <p key={i} className="text-[11px] text-zinc-400 leading-snug">{s}</p>
                    ))}
                  </div>
                )}
                {state.status === "error" && state.error && (
                  <p className="pl-5 text-[11px] text-rose-400">{state.error}</p>
                )}
              </div>
            ))}
          </div>
          <div className="px-6 py-4 border-t border-zinc-800">
            <Button
              size="sm"
              variant="ghost"
              className="w-full"
              onClick={() => {
                setDrawerOpen(false);
                fetchProducts();
                setRowSelection({});
              }}
            >
              <RefreshCcw className="h-3.5 w-3.5 mr-1.5" />
              Close & Refresh Table
            </Button>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}

function SkuStatusIcon({ status }: { status: SkuStatus }) {
  switch (status) {
    case "pending":
      return <span className="h-3.5 w-3.5 rounded-full border border-zinc-600 flex-shrink-0" />;
    case "running":
      return <Loader2 className="h-3.5 w-3.5 animate-spin text-amber-400 flex-shrink-0" />;
    case "done":
      return <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400 flex-shrink-0" />;
    case "error":
      return <AlertCircle className="h-3.5 w-3.5 text-rose-400 flex-shrink-0" />;
  }
}

// ═══════════════════════════════════════════════════════════════════════════════
// Tab 2 — Campaign Builder
// ═══════════════════════════════════════════════════════════════════════════════

const THEME_OPTIONS: { key: CampaignTheme; label: string }[] = [
  { key: "product_launch", label: "Product Launch" },
  { key: "tech_refresh", label: "Tech Refresh" },
  { key: "clearance_sale", label: "Clearance Sale" },
  { key: "holiday_q4", label: "Holiday / Q4" },
  { key: "back_to_school", label: "Back to School" },
];

const AUDIENCE_OPTIONS: { key: CampaignAudience; label: string }[] = [
  { key: "it_managers", label: "IT Managers" },
  { key: "smb_owners", label: "SMB Owners" },
  { key: "procurement_teams", label: "Procurement Teams" },
];

function PillToggle<T extends string>({
  options,
  value,
  onChange,
}: {
  options: { key: T; label: string }[];
  value: T;
  onChange: (v: T) => void;
}) {
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((o) => (
        <button
          key={o.key}
          onClick={() => onChange(o.key)}
          className={cn(
            "px-3 py-1 rounded-full text-xs border transition-all",
            value === o.key
              ? "bg-emerald-600 border-emerald-500 text-white"
              : "border-border bg-background/30 text-muted-foreground hover:border-emerald-700 hover:text-foreground",
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

function CampaignBuilder() {
  // Step 1
  const [theme, setTheme] = useState<CampaignTheme>("product_launch");
  const [audience, setAudience] = useState<CampaignAudience>("it_managers");
  const [campaignName, setCampaignName] = useState("");

  // Step 2 — SKU picker
  const [query, setQuery] = useState("");
  const [searchResults, setSearchResults] = useState<CuratedProduct[]>([]);
  const [searchOpen, setSearchOpen] = useState(false);
  const [selectedSkus, setSelectedSkus] = useState<CampaignSku[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);

  // Step 3 — generation
  const [generating, setGenerating] = useState(false);
  const [output, setOutput] = useState<CampaignOutput | null>(null);
  const [outputTab, setOutputTab] = useState("email");
  const [editedLinkedIn, setEditedLinkedIn] = useState("");

  // search products
  useEffect(() => {
    if (query.trim().length < 2) {
      setSearchResults([]);
      setSearchOpen(false);
      return;
    }
    const t = setTimeout(async () => {
      setSearchLoading(true);
      try {
        const res = await fetch(`/api/products?q=${encodeURIComponent(query)}&limit=20`, {
          headers: { Accept: "application/json" },
        });
        if (!res.ok) throw new Error(`${res.status}`);
        const data = await res.json();
        setSearchResults(Array.isArray(data) ? data : data.items ?? []);
        setSearchOpen(true);
      } catch {
        setSearchResults([]);
      } finally {
        setSearchLoading(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [query]);

  // close dropdown on outside click
  useEffect(() => {
    function handler(e: MouseEvent) {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        setSearchOpen(false);
      }
    }
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  function addSku(p: CuratedProduct) {
    if (selectedSkus.length >= 10) {
      toast.error("Max 10 SKUs per campaign");
      return;
    }
    if (selectedSkus.find((s) => s.sku === p.tdsynnex_sku)) return;
    setSelectedSkus((prev) => [
      ...prev,
      {
        sku: p.tdsynnex_sku,
        description: p.short_description ?? p.title ?? "",
        brand: p.brand_slug ?? "",
        msrp: 0,
      },
    ]);
    setQuery("");
    setSearchOpen(false);
  }

  async function loadTopPicks() {
    try {
      const res = await fetch("/api/analytics/score/top?limit=5&preset=profitable", {
        headers: { Accept: "application/json" },
      });
      if (!res.ok) throw new Error(`${res.status}`);
      const data = await res.json();
      const items: CuratedProduct[] = Array.isArray(data) ? data : data.items ?? [];
      items.forEach((p) => addSku(p));
      toast.success(`Loaded ${Math.min(items.length, 5)} top picks`);
    } catch (err) {
      toast.error(`Failed to load top picks: ${String(err)}`);
    }
  }

  async function generate() {
    if (selectedSkus.length === 0) {
      toast.error("Select at least one SKU");
      return;
    }

    setGenerating(true);
    setOutput(null);

    try {
      const res = await fetch("/api/marketing/campaign", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          theme,
          audience,
          name: campaignName || undefined,
          skus: selectedSkus,
        }),
      });

      if (res.status === 404) {
        toast.error("Campaign endpoint not available yet — coming soon.");
        return;
      }

      if (!res.ok) {
        const body = await res.text();
        throw new Error(body || `${res.status} ${res.statusText}`);
      }

      const data: CampaignOutput = await res.json();
      setOutput(data);
      setEditedLinkedIn(data.linkedin?.post ?? "");
      setOutputTab("email");
      toast.success("Campaign generated");
    } catch (err) {
      if (!(String(err).includes("404"))) {
        toast.error(`Generation failed: ${String(err)}`);
      }
    } finally {
      setGenerating(false);
    }
  }

  function CharCounter({ text, max }: { text: string; max: number }) {
    const n = text.length;
    return (
      <span
        className={cn(
          "text-[10px] tabular-nums",
          n > max ? "text-rose-500" : n > max * 0.85 ? "text-amber-500" : "text-muted-foreground",
        )}
      >
        {n}/{max}
      </span>
    );
  }

  return (
    <div className="space-y-6">
      {/* Step 1 */}
      <section className="rounded-lg border border-border bg-zinc-900/40 p-5 space-y-4">
        <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
          <span className="h-5 w-5 rounded-full bg-emerald-600/30 text-emerald-400 text-[10px] flex items-center justify-center font-bold">1</span>
          Configure
        </h3>
        <div className="space-y-3">
          <div>
            <p className="text-xs text-muted-foreground mb-2">Campaign Theme</p>
            <PillToggle options={THEME_OPTIONS} value={theme} onChange={setTheme} />
          </div>
          <div>
            <p className="text-xs text-muted-foreground mb-2">Target Audience</p>
            <PillToggle options={AUDIENCE_OPTIONS} value={audience} onChange={setAudience} />
          </div>
          <div>
            <p className="text-xs text-muted-foreground mb-1.5">Campaign Name (optional)</p>
            <Input
              placeholder="e.g. Q2 Dell Refresh — SMB"
              value={campaignName}
              onChange={(e) => setCampaignName(e.target.value)}
              className="h-8 text-xs bg-background/50 max-w-sm"
            />
          </div>
        </div>
      </section>

      {/* Step 2 */}
      <section className="rounded-lg border border-border bg-zinc-900/40 p-5 space-y-4">
        <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
          <span className="h-5 w-5 rounded-full bg-emerald-600/30 text-emerald-400 text-[10px] flex items-center justify-center font-bold">2</span>
          Pick SKUs
          <span className="ml-auto text-[10px] text-muted-foreground">{selectedSkus.length}/10</span>
        </h3>

        <div className="flex gap-2">
          {/* Search */}
          <div ref={searchRef} className="relative flex-1">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground z-10" />
            <Input
              placeholder="Search products…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onFocus={() => searchResults.length > 0 && setSearchOpen(true)}
              className="pl-8 h-8 text-xs bg-background/50"
            />
            {searchOpen && (
              <div className="absolute z-50 mt-1 w-full rounded-md border border-border bg-zinc-900 shadow-xl max-h-64 overflow-y-auto">
                {searchLoading ? (
                  <div className="flex justify-center py-4">
                    <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                  </div>
                ) : searchResults.length === 0 ? (
                  <p className="text-xs text-muted-foreground px-3 py-2">No results</p>
                ) : (
                  searchResults.map((p) => (
                    <button
                      key={p.tdsynnex_sku}
                      onClick={() => addSku(p)}
                      className="w-full flex items-start gap-3 px-3 py-2 text-left hover:bg-muted/20 transition-colors"
                    >
                      <span className="font-mono text-[10px] text-emerald-400 mt-0.5 shrink-0">{p.tdsynnex_sku}</span>
                      <span className="text-xs text-foreground/80 leading-snug">{truncate(p.title ?? p.short_description ?? "", 60)}</span>
                    </button>
                  ))
                )}
              </div>
            )}
          </div>

          <Button
            size="sm"
            variant="outline"
            onClick={loadTopPicks}
            className="gap-1.5 text-xs border-emerald-800 text-emerald-400 hover:bg-emerald-900/30 shrink-0"
          >
            <Zap className="h-3 w-3" />
            Top Picks
          </Button>
        </div>

        {/* Selected chips */}
        {selectedSkus.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {selectedSkus.map((s) => (
              <div
                key={s.sku}
                className="flex items-center gap-1.5 rounded-full bg-zinc-800 border border-zinc-700 pl-3 pr-1.5 py-0.5"
              >
                <span className="font-mono text-[11px] text-emerald-400">{s.sku}</span>
                <button
                  onClick={() => setSelectedSkus((prev) => prev.filter((x) => x.sku !== s.sku))}
                  className="rounded-full hover:bg-zinc-600 transition-colors p-0.5"
                >
                  <X className="h-3 w-3 text-muted-foreground" />
                </button>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Step 3 */}
      <section className="rounded-lg border border-border bg-zinc-900/40 p-5 space-y-4">
        <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
          <span className="h-5 w-5 rounded-full bg-emerald-600/30 text-emerald-400 text-[10px] flex items-center justify-center font-bold">3</span>
          Generate
        </h3>

        <Button
          onClick={generate}
          disabled={generating || selectedSkus.length === 0}
          className="bg-emerald-600 hover:bg-emerald-500 text-white gap-2"
        >
          {generating ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Generating with Claude…
            </>
          ) : (
            <>
              <Sparkles className="h-4 w-4" />
              Generate Campaign
            </>
          )}
        </Button>
      </section>

      {/* Output */}
      {output && (
        <section className="rounded-lg border border-emerald-900/60 bg-zinc-900/40 p-5 space-y-4">
          <h3 className="text-sm font-semibold text-emerald-400">Campaign Output</h3>
          <Tabs value={outputTab} onValueChange={setOutputTab}>
            <TabsList className="bg-zinc-800/60 h-8">
              {["email", "linkedin", "google_ads", "highlights"].map((k) => (
                <TabsTrigger key={k} value={k} className="text-xs px-3 h-7">
                  {k === "google_ads" ? "Google Ads" : k === "highlights" ? "Highlights" : k.charAt(0).toUpperCase() + k.slice(1)}
                </TabsTrigger>
              ))}
            </TabsList>

            {/* Email */}
            <TabsContent value="email" className="mt-4 space-y-3">
              {output.email ? (
                <>
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1">
                      <p className="text-[10px] text-muted-foreground uppercase tracking-wide mb-1">Subject Line</p>
                      <p className="text-sm font-medium text-foreground">{output.email.subject}</p>
                    </div>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => copyToClipboard(output.email!.subject, "Subject")}
                      className="gap-1 text-xs"
                    >
                      <ClipboardCopy className="h-3.5 w-3.5" />
                      Copy
                    </Button>
                  </div>
                  <div className="rounded-md border border-border bg-zinc-950/60 p-4">
                    <pre className="text-xs text-foreground/80 whitespace-pre-wrap font-sans leading-relaxed">
                      {output.email.body}
                    </pre>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => copyToClipboard(output.email!.body, "Email body")}
                    className="gap-1.5 text-xs"
                  >
                    <ClipboardCopy className="h-3.5 w-3.5" />
                    Copy Full Email
                  </Button>
                </>
              ) : (
                <p className="text-sm text-muted-foreground">No email content generated.</p>
              )}
            </TabsContent>

            {/* LinkedIn */}
            <TabsContent value="linkedin" className="mt-4 space-y-3">
              {output.linkedin ? (
                <>
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] text-muted-foreground">
                      Character count: {editedLinkedIn.length} / 3000
                    </span>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => copyToClipboard(editedLinkedIn, "LinkedIn post")}
                      className="gap-1 text-xs"
                    >
                      <ClipboardCopy className="h-3.5 w-3.5" />
                      Copy
                    </Button>
                  </div>
                  <textarea
                    value={editedLinkedIn}
                    onChange={(e) => setEditedLinkedIn(e.target.value)}
                    rows={10}
                    className="w-full rounded-md border border-border bg-zinc-950/60 text-xs text-foreground/90 p-3 resize-none focus:outline-none focus:ring-1 focus:ring-emerald-600 leading-relaxed font-sans"
                  />
                </>
              ) : (
                <p className="text-sm text-muted-foreground">No LinkedIn content generated.</p>
              )}
            </TabsContent>

            {/* Google Ads */}
            <TabsContent value="google_ads" className="mt-4 space-y-4">
              {output.google_ads ? (
                <>
                  <div className="space-y-2">
                    <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Headlines</p>
                    {(["headline1", "headline2", "headline3"] as const).map((k, i) => (
                      <div key={k} className="flex items-center gap-3 rounded-md bg-zinc-950/50 border border-border px-3 py-2">
                        <span className="text-[10px] text-muted-foreground w-8">H{i + 1}</span>
                        <span className="text-xs text-foreground flex-1">{output.google_ads![k]}</span>
                        <CharCounter text={output.google_ads![k]} max={30} />
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-5 w-5"
                          onClick={() => copyToClipboard(output.google_ads![k])}
                        >
                          <ClipboardCopy className="h-3 w-3" />
                        </Button>
                      </div>
                    ))}
                  </div>
                  <div className="space-y-2">
                    <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Descriptions</p>
                    {(["description1", "description2"] as const).map((k, i) => (
                      <div key={k} className="flex items-start gap-3 rounded-md bg-zinc-950/50 border border-border px-3 py-2">
                        <span className="text-[10px] text-muted-foreground w-8 mt-0.5">D{i + 1}</span>
                        <span className="text-xs text-foreground flex-1 leading-snug">{output.google_ads![k]}</span>
                        <div className="flex items-center gap-1 shrink-0">
                          <CharCounter text={output.google_ads![k]} max={90} />
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-5 w-5"
                            onClick={() => copyToClipboard(output.google_ads![k])}
                          >
                            <ClipboardCopy className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <p className="text-sm text-muted-foreground">No Google Ads content generated.</p>
              )}
            </TabsContent>

            {/* Product Highlights */}
            <TabsContent value="highlights" className="mt-4">
              {output.product_highlights && output.product_highlights.length > 0 ? (
                <div className="grid gap-3 sm:grid-cols-2">
                  {output.product_highlights.map((h) => (
                    <div key={h.sku} className="rounded-lg border border-border bg-zinc-950/60 p-4 space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="font-mono text-[10px] text-emerald-400">{h.sku}</span>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-5 w-5"
                          onClick={() =>
                            copyToClipboard(
                              `${h.tagline}\n• ${h.bullets.join("\n• ")}`,
                              "Highlight",
                            )
                          }
                        >
                          <ClipboardCopy className="h-3 w-3" />
                        </Button>
                      </div>
                      <p className="text-xs font-medium text-foreground">{h.tagline}</p>
                      <ul className="space-y-1">
                        {h.bullets.map((b, i) => (
                          <li key={i} className="flex items-start gap-1.5 text-[11px] text-muted-foreground">
                            <span className="text-emerald-500 mt-0.5">•</span>
                            {b}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">No product highlights generated.</p>
              )}
            </TabsContent>
          </Tabs>
        </section>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// Tab 3 — SEO Audit
// ═══════════════════════════════════════════════════════════════════════════════

interface SeoProduct extends CuratedProduct {
  seoScore: number;
}

function SeoAudit() {
  const [products, setProducts] = useState<SeoProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [sorting, setSorting] = useState<SortingState>([{ id: "seoScore", desc: false }]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [pagination, setPagination] = useState<PaginationState>({ pageIndex: 0, pageSize: 20 });

  // Fix sheet
  const [fixProduct, setFixProduct] = useState<SeoProduct | null>(null);
  const [fixing, setFixing] = useState(false);
  const [fixSteps, setFixSteps] = useState<string[]>([]);
  const fixCleanup = useRef<(() => void) | null>(null);

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch("/api/curated?status=published", {
        headers: { Accept: "application/json" },
      });
      if (!res.ok) throw new Error(`${res.status}`);
      const data = await res.json();
      const items: CuratedProduct[] = Array.isArray(data) ? data : data.items ?? [];
      setProducts(
        items.map((p) => ({ ...p, seoScore: computeSeoScore(p) })),
      );
    } catch (err) {
      toast.error(`Failed to load: ${String(err)}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const { perfect, needsWork, critical } = useMemo(() => {
    return {
      perfect: products.filter((p) => p.seoScore >= 75).length,
      needsWork: products.filter((p) => p.seoScore >= 50 && p.seoScore < 75).length,
      critical: products.filter((p) => p.seoScore < 50).length,
    };
  }, [products]);

  const columns = useMemo<ColumnDef<SeoProduct>[]>(
    () => [
      {
        accessorKey: "tdsynnex_sku",
        header: "SKU",
        cell: ({ getValue }) => (
          <span className="font-mono text-[11px] text-emerald-400 tabular-nums">{String(getValue())}</span>
        ),
      },
      {
        accessorKey: "title",
        header: "Title",
        cell: ({ getValue }) => (
          <span className="text-xs text-foreground/90">{truncate(String(getValue() ?? "—"), 50)}</span>
        ),
      },
      {
        id: "seoScore",
        accessorKey: "seoScore",
        header: ({ column }) => (
          <button
            className="flex items-center gap-1 text-xs uppercase tracking-wide hover:text-foreground transition-colors"
            onClick={() => column.toggleSorting()}
          >
            SEO Score <ArrowUpDown className="h-3 w-3" />
          </button>
        ),
        cell: ({ getValue }) => <ScoreBadge score={getValue() as number} />,
        sortingFn: "basic",
      },
      {
        id: "seo_title_ok",
        header: "Title",
        accessorFn: (r) => {
          const l = (r.seo_title ?? "").length;
          return l >= 30 && l <= 60;
        },
        cell: ({ getValue }) => <BoolCheck val={getValue() as boolean} />,
      },
      {
        id: "seo_desc_ok",
        header: "Meta",
        accessorFn: (r) => {
          const l = (r.seo_description ?? "").length;
          return l >= 100 && l <= 155;
        },
        cell: ({ getValue }) => <BoolCheck val={getValue() as boolean} />,
      },
      {
        id: "body_ok",
        header: "Body",
        accessorFn: (r) => (r.body_html ?? "").length > 500,
        cell: ({ getValue }) => <BoolCheck val={getValue() as boolean} />,
      },
      {
        id: "tags_count",
        header: "Tags",
        accessorFn: (r) => (r.tags ?? []).length,
        cell: ({ getValue }) => (
          <span className="text-[11px] tabular-nums text-muted-foreground">{String(getValue())}</span>
        ),
      },
      {
        id: "fix",
        header: "",
        cell: ({ row }) => (
          <Button
            size="sm"
            variant="ghost"
            onClick={(e) => {
              e.stopPropagation();
              setFixProduct(row.original);
              setFixSteps([]);
            }}
            className="h-6 px-2 text-[11px] border border-border hover:border-emerald-700 hover:text-emerald-400"
          >
            Fix
          </Button>
        ),
      },
    ],
    [],
  );

  const table = useReactTable({
    data: products,
    columns,
    state: { sorting, columnFilters, pagination },
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  function runFix() {
    if (!fixProduct) return;
    setFixing(true);
    setFixSteps([]);

    const cancel = streamCurate(
      fixProduct.tdsynnex_sku,
      (step) => setFixSteps((prev) => [...prev, step]),
      async (data) => {
        try {
          const res = await fetch(`/api/curated/${fixProduct.id}`, {
            method: "PATCH",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              seo_title: data.seo_title,
              seo_description: data.seo_description,
            }),
          });
          if (!res.ok) throw new Error(`PATCH ${res.status}`);

          // update local row
          setProducts((prev) =>
            prev.map((p) => {
              if (p.id !== fixProduct.id) return p;
              const updated: SeoProduct = {
                ...p,
                seo_title: (data.seo_title as string) ?? p.seo_title,
                seo_description: (data.seo_description as string) ?? p.seo_description,
              };
              return { ...updated, seoScore: computeSeoScore(updated) };
            }),
          );

          toast.success(`SEO updated for ${fixProduct.tdsynnex_sku}`);
        } catch (err) {
          toast.error(`PATCH failed: ${String(err)}`);
        } finally {
          setFixing(false);
        }
      },
      (err) => {
        toast.error(`Stream error: ${err}`);
        setFixing(false);
      },
    );
    fixCleanup.current = cancel;
  }

  return (
    <div className="space-y-4">
      {/* Summary cards */}
      <div className="grid grid-cols-3 gap-3">
        {[
          { label: "Perfect (75–100)", count: perfect, color: "text-emerald-400", bg: "border-emerald-900/40 bg-emerald-950/20" },
          { label: "Needs Work (50–74)", count: needsWork, color: "text-amber-400", bg: "border-amber-900/40 bg-amber-950/20" },
          { label: "Critical (<50)", count: critical, color: "text-rose-400", bg: "border-rose-900/40 bg-rose-950/20" },
        ].map(({ label, count, color, bg }) => (
          <div key={label} className={cn("rounded-lg border p-4", bg)}>
            <p className="text-xs text-muted-foreground mb-1">{label}</p>
            <p className={cn("text-2xl font-bold tabular-nums", color)}>{loading ? "…" : count}</p>
          </div>
        ))}
      </div>

      {/* Refresh */}
      <div className="flex justify-end">
        <Button size="sm" variant="ghost" onClick={fetchProducts} className="gap-1.5">
          <RefreshCcw className="h-3.5 w-3.5" />
          Refresh
        </Button>
      </div>

      {/* Table */}
      <div className="rounded-lg border border-border overflow-hidden">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((hg) => (
              <TableRow key={hg.id} className="border-border hover:bg-transparent">
                {hg.headers.map((h) => (
                  <TableHead key={h.id} className="text-muted-foreground text-xs h-9">
                    {h.isPlaceholder ? null : flexRender(h.column.columnDef.header, h.getContext())}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-24 text-center">
                  <Loader2 className="h-5 w-5 animate-spin mx-auto text-muted-foreground" />
                </TableCell>
              </TableRow>
            ) : table.getRowModel().rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-20 text-center text-sm text-muted-foreground">
                  No published products found.
                </TableCell>
              </TableRow>
            ) : (
              table.getRowModel().rows.map((row) => (
                <TableRow key={row.id} className="border-border hover:bg-muted/20">
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id} className="py-2">
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <span>{table.getFilteredRowModel().rows.length} published products</span>
        <div className="flex items-center gap-1">
          <Button
            size="icon"
            variant="ghost"
            className="h-6 w-6"
            disabled={!table.getCanPreviousPage()}
            onClick={() => table.previousPage()}
          >
            <ChevronLeft className="h-3.5 w-3.5" />
          </Button>
          <span>{pagination.pageIndex + 1} / {table.getPageCount() || 1}</span>
          <Button
            size="icon"
            variant="ghost"
            className="h-6 w-6"
            disabled={!table.getCanNextPage()}
            onClick={() => table.nextPage()}
          >
            <ChevronRight className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>

      {/* Fix Sheet */}
      <Sheet
        open={!!fixProduct}
        onOpenChange={(open) => {
          if (!open) {
            fixCleanup.current?.();
            setFixProduct(null);
            setFixing(false);
            setFixSteps([]);
          }
        }}
      >
        <SheetContent side="right" className="w-[420px] bg-zinc-950 border-zinc-800 flex flex-col gap-0 p-0">
          <SheetHeader className="px-6 py-4 border-b border-zinc-800">
            <SheetTitle className="text-sm font-semibold text-foreground flex items-center gap-2">
              <Tag className="h-4 w-4 text-emerald-400" />
              SEO Fix
              {fixProduct && (
                <span className="ml-2 font-mono text-[10px] text-emerald-400">{fixProduct.tdsynnex_sku}</span>
              )}
            </SheetTitle>
          </SheetHeader>

          {fixProduct && (
            <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
              {/* Product info */}
              <div className="space-y-1">
                <p className="text-xs font-medium text-foreground">{fixProduct.title ?? "—"}</p>
                <div className="flex items-center gap-2">
                  <ScoreBadge score={fixProduct.seoScore} />
                  <span className="text-[10px] text-muted-foreground">current score</span>
                </div>
              </div>

              {/* Current SEO values */}
              <div className="space-y-3 rounded-md border border-zinc-800 bg-zinc-900/40 p-3">
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-[10px] text-muted-foreground uppercase tracking-wide">SEO Title</p>
                    <span
                      className={cn(
                        "text-[10px] tabular-nums",
                        (fixProduct.seo_title ?? "").length >= 30 && (fixProduct.seo_title ?? "").length <= 60
                          ? "text-emerald-400"
                          : "text-rose-400",
                      )}
                    >
                      {(fixProduct.seo_title ?? "").length}/60
                    </span>
                  </div>
                  <p className="text-xs text-foreground/80">{fixProduct.seo_title ?? <span className="italic text-muted-foreground">Missing</span>}</p>
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Meta Description</p>
                    <span
                      className={cn(
                        "text-[10px] tabular-nums",
                        (fixProduct.seo_description ?? "").length >= 100 && (fixProduct.seo_description ?? "").length <= 155
                          ? "text-emerald-400"
                          : "text-rose-400",
                      )}
                    >
                      {(fixProduct.seo_description ?? "").length}/155
                    </span>
                  </div>
                  <p className="text-xs text-foreground/80 leading-snug">
                    {fixProduct.seo_description ?? <span className="italic text-muted-foreground">Missing</span>}
                  </p>
                </div>
              </div>

              {/* Steps log */}
              {fixSteps.length > 0 && (
                <div className="rounded-md border border-zinc-800 bg-zinc-900/40 p-3 space-y-1 max-h-48 overflow-y-auto">
                  {fixSteps.map((s, i) => (
                    <p key={i} className="text-[11px] text-zinc-400 leading-snug">{s}</p>
                  ))}
                  {fixing && <Loader2 className="h-3.5 w-3.5 animate-spin text-emerald-400 mt-1" />}
                </div>
              )}

              <Button
                onClick={runFix}
                disabled={fixing}
                className="w-full bg-emerald-600 hover:bg-emerald-500 text-white gap-2"
              >
                {fixing ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Regenerating SEO…
                  </>
                ) : (
                  <>
                    <Sparkles className="h-4 w-4" />
                    Regenerate SEO
                  </>
                )}
              </Button>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// Root — MarketingPage
// ═══════════════════════════════════════════════════════════════════════════════

export function MarketingPage() {
  return (
    <div className="flex flex-col gap-6 p-6 min-h-full">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-xl font-semibold tracking-tight text-foreground flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-emerald-400" />
            Marketing Hub
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Content factory · Campaign builder · SEO audit
          </p>
        </div>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="content" className="flex-1">
        <TabsList className="bg-zinc-900/60 border border-zinc-800 h-9 w-fit">
          <TabsTrigger value="content" className="text-xs px-4 h-8">
            Content Factory
          </TabsTrigger>
          <TabsTrigger value="campaign" className="text-xs px-4 h-8">
            Campaign Builder
          </TabsTrigger>
          <TabsTrigger value="seo" className="text-xs px-4 h-8">
            SEO Audit
          </TabsTrigger>
        </TabsList>

        <TabsContent value="content" className="mt-6">
          <ContentFactory />
        </TabsContent>

        <TabsContent value="campaign" className="mt-6">
          <CampaignBuilder />
        </TabsContent>

        <TabsContent value="seo" className="mt-6">
          <SeoAudit />
        </TabsContent>
      </Tabs>
    </div>
  );
}
